import { Link } from "react-router-dom";
import type { WorkItem } from "@/mocks/works";

interface WorkCardProps {
  item: WorkItem;
  variant?: "poster" | "wide" | "mini";
}

export default function WorkCard({ item, variant = "poster" }: WorkCardProps) {
  if (variant === "mini") {
    return (
      <Link
        to={`/works/${item.id}`}
        className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
      >
        <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={item.cover}
            alt={`${item.title} 海报 - 张柏芝影视作品`}
            title={`${item.title} - 张柏芝影视作品`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <h4 className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
            {item.title}
          </h4>
          <p className="mt-1 text-xs text-foreground-500">
            {item.year} · {item.type}
          </p>
          <p className="mt-1 truncate text-xs text-foreground-600">{item.role}</p>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/works/${item.id}`}
        className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 sm:grid-cols-[180px_1fr]"
      >
        <div className="h-52 w-full overflow-hidden bg-background-200 sm:h-full">
          <img
            src={item.cover}
            alt={`${item.title} 海报 - 张柏芝影视作品`}
            title={`${item.title} - 张柏芝影视作品`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col gap-2 p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
            <span className="rounded-sm bg-accent-100 px-2 py-0.5 text-accent-900">{item.type}</span>
            <span>{item.year}</span>
            <span className="text-foreground-300">·</span>
            <span>导演 {item.director}</span>
          </div>
          <h3 className="font-heading text-lg font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
            {item.title}
          </h3>
          <p className="text-xs text-foreground-600">饰演 {item.role}</p>
          <p className="line-clamp-3 text-[13px] leading-6 text-foreground-600">{item.synopsis}</p>
          <span className="mt-auto flex items-center gap-1 pt-2 text-xs text-primary-700">
            查看资料
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/works/${item.id}`} className="group block">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={item.cover}
          alt={`${item.title} 海报 - 张柏芝影视作品`}
          title={`${item.title} - 张柏芝影视作品`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent p-4 pt-12">
          <p className="text-[11px] text-background-100">
            {item.year} · {item.type}
          </p>
          <h3 className="mt-1 font-heading text-base font-semibold text-background-50">{item.title}</h3>
          <p className="mt-0.5 line-clamp-1 text-[11px] text-background-200">饰演 {item.role}</p>
        </div>
        <span className="absolute right-3 top-3 rounded-full bg-background-50/90 px-2 py-0.5 text-[10px] text-foreground-800">
          {item.highlight}
        </span>
      </div>
    </Link>
  );
}